import React, { useState } from 'react';
import { X, FileCheck, Download, ExternalLink, Calendar, User, CreditCard, Phone, ZoomIn, ZoomOut, RotateCw } from 'lucide-react';

interface ModalVerDeclaracionJuradaProps {
    isOpen: boolean;
    onClose: () => void;
    cliente?: {
        nombre?: string;
        email?: string;
        telefono?: string | null;
        ci_nit?: string | null;
        tipo_cliente?: string;
        fecha_declaracion_jurada?: string | null;
        foto_declaracion_jurada_url?: string | null;
    } | null;
    fotoUrl?: string | null;
    clienteNombre?: string;
    fechaFirma?: string | null;
    tipoNivel?: string;
    ci_nit?: string | null;
    telefono?: string | null;
}

const ModalVerDeclaracionJurada: React.FC<ModalVerDeclaracionJuradaProps> = ({
    isOpen,
    onClose,
    cliente,
    fotoUrl,
    clienteNombre,
    fechaFirma,
    tipoNivel,
    ci_nit,
    telefono
}) => {
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [isDownloading, setIsDownloading] = useState(false);

    if (!isOpen) return null;

    const dataCliente = cliente || {
        nombre: clienteNombre || 'Cliente',
        email: undefined,
        telefono: telefono || null,
        ci_nit: ci_nit || null,
        tipo_cliente: tipoNivel,
        fecha_declaracion_jurada: fechaFirma || null,
        foto_declaracion_jurada_url: fotoUrl || null
    };

    const fotoUrlFinal = dataCliente.foto_declaracion_jurada_url;

    const fechaFormateada = dataCliente.fecha_declaracion_jurada
        ? new Date(dataCliente.fecha_declaracion_jurada).toLocaleString('es-ES', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        })
        : 'Fecha no registrada';

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.25, 3));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.25, 0.5));
    const handleRotate = () => setRotation(prev => (prev + 90) % 360);
    const handleReset = () => {
        setZoom(1);
        setRotation(0);
    };

    const handleDescargarFoto = async () => {
        if (!fotoUrlFinal) return;
        setIsDownloading(true);
        try {
            const response = await fetch(fotoUrlFinal);
            if (!response.ok) throw new Error('Network response was not ok');
            const blob = await response.blob();
            const blobUrl = window.URL.createObjectURL(blob);
            
            // Detectar extensión del archivo
            const urlPath = fotoUrlFinal.split('?')[0];
            const extMatch = urlPath.match(/\.([a-zA-Z0-9]+)$/);
            const ext = extMatch ? extMatch[1] : 'webp';
            
            const sanitizedName = (dataCliente.nombre || 'Cliente').replace(/[^a-zA-Z0-9_-]/g, '_');
            const fileName = `Declaracion_Jurada_${sanitizedName}.${ext}`;

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(blobUrl);
        } catch (error) {
            /* Browser diagnostics omitted. */
            // Fallback: abrir en nueva ventana/descarga nativa
            const link = document.createElement('a');
            link.href = fotoUrlFinal;
            link.download = `Declaracion_Jurada_${(dataCliente.nombre || 'Cliente').replace(/\s+/g, '_')}.webp`;
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } finally {
            setIsDownloading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6">
            {/* Backdrop */}
            <div 
                className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
                onClick={onClose}
            />

            {/* Modal Dialog */}
            <div 
                className="relative bg-white w-full max-w-4xl rounded-2xl shadow-2xl border border-slate-200 overflow-hidden flex flex-col max-h-[90vh] z-10 animate-in fade-in zoom-in-95 duration-200"
                onClick={e => e.stopPropagation()}
            >
                {/* Top Accent Bar (Brand primary/secondary) */}
                <div className="h-1.5 w-full bg-gradient-to-r from-[#8f1f64] via-[#c1348b] to-[#61257b]" />

                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100 bg-slate-50/50">
                    <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-xl bg-[#8f1f64]/10 border border-[#8f1f64]/20 flex items-center justify-center text-[#8f1f64]">
                            <FileCheck className="h-5 w-5" />
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-lg font-bold text-slate-900">Declaración Jurada Firmada</h3>
                                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    ✓ Acreditada
                                </span>
                            </div>
                            <p className="text-xs text-slate-500">Documento de respaldo y firma legal del cliente</p>
                        </div>
                    </div>

                    <button
                        onClick={onClose}
                        className="h-8 w-8 rounded-lg flex items-center justify-center text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Client Information Card */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 bg-slate-50 p-4 rounded-xl border border-slate-200/80">
                        <div>
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                <User className="h-3 w-3 text-[#8f1f64]" /> Cliente
                            </span>
                            <p className="text-sm font-bold text-slate-800 truncate" title={dataCliente.nombre || 'No asignado'}>
                                {dataCliente.nombre || 'No asignado'}
                            </p>
                            {dataCliente.tipo_cliente && (
                                <span className="inline-block mt-0.5 px-2 py-0.5 rounded text-[10px] font-bold bg-[#61257b]/10 text-[#61257b]">
                                    {dataCliente.tipo_cliente}
                                </span>
                            )}
                        </div>

                        <div>
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                <CreditCard className="h-3 w-3 text-[#61257b]" /> CI / NIT
                            </span>
                            <p className="text-sm font-semibold text-slate-800">
                                {dataCliente.ci_nit || '—'}
                            </p>
                        </div>

                        <div>
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                <Phone className="h-3 w-3 text-[#c1348b]" /> Teléfono
                            </span>
                            <p className="text-sm font-semibold text-slate-800">
                                {dataCliente.telefono || '—'}
                            </p>
                        </div>

                        <div>
                            <span className="text-[11px] font-semibold text-slate-400 uppercase tracking-wider mb-0.5 flex items-center gap-1">
                                <Calendar className="h-3 w-3 text-emerald-600" /> Fecha de Firma
                            </span>
                            <p className="text-xs font-semibold text-slate-800">
                                {fechaFormateada}
                            </p>
                        </div>
                    </div>

                    {/* Photo / Document Viewer Section */}
                    <div className="border border-slate-200 rounded-xl overflow-hidden bg-slate-950/5 flex flex-col">
                        {/* Viewer Toolbar */}
                        <div className="flex items-center justify-between px-4 py-2 bg-slate-100 border-b border-slate-200 text-xs text-slate-600">
                            <span className="font-semibold text-slate-700">Evidencia Fotográfica de la Firma</span>
                            {fotoUrlFinal && (
                                <div className="flex items-center gap-1.5">
                                    <button 
                                        type="button"
                                        onClick={handleZoomIn} 
                                        title="Acercar"
                                        className="p-1.5 rounded hover:bg-white text-slate-600 hover:text-slate-900 transition-colors shadow-xs"
                                    >
                                        <ZoomIn className="h-4 w-4" />
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleZoomOut} 
                                        title="Alejar"
                                        className="p-1.5 rounded hover:bg-white text-slate-600 hover:text-slate-900 transition-colors shadow-xs"
                                    >
                                        <ZoomOut className="h-4 w-4" />
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleRotate} 
                                        title="Rotar"
                                        className="p-1.5 rounded hover:bg-white text-slate-600 hover:text-slate-900 transition-colors shadow-xs"
                                    >
                                        <RotateCw className="h-4 w-4" />
                                    </button>
                                    <button 
                                        type="button"
                                        onClick={handleReset} 
                                        className="px-2 py-1 rounded hover:bg-white text-slate-600 text-[11px] font-medium transition-colors shadow-xs"
                                    >
                                        Reset ({Math.round(zoom * 100)}%)
                                    </button>
                                    <a
                                        href={fotoUrlFinal}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1.5 rounded hover:bg-white text-[#61257b] hover:text-[#61257b]/80 transition-colors shadow-xs ml-2 flex items-center gap-1 font-medium"
                                        title="Abrir imagen original en nueva pestaña"
                                    >
                                        <ExternalLink className="h-4 w-4" />
                                    </a>
                                </div>
                            )}
                        </div>

                        {/* Image Display Area */}
                        <div className="relative min-h-[340px] max-h-[500px] flex items-center justify-center p-4 overflow-hidden bg-slate-900/5">
                            {fotoUrlFinal ? (
                                <div className="overflow-auto max-h-[480px] w-full flex items-center justify-center">
                                    <img
                                        src={fotoUrlFinal}
                                        alt={`Declaración jurada de ${dataCliente.nombre}`}
                                        style={{
                                            transform: `scale(${zoom}) rotate(${rotation}deg)`,
                                            transition: 'transform 0.2s ease-out'
                                        }}
                                        className="max-h-[450px] w-auto object-contain rounded-lg shadow-md border border-white"
                                    />
                                </div>
                            ) : (
                                <div className="text-center p-8 text-slate-400">
                                    <FileCheck className="h-12 w-12 mx-auto mb-2 text-slate-300 stroke-1" />
                                    <p className="text-sm font-medium text-slate-600">Declaración jurada registrada sin foto adjunta</p>
                                    <p className="text-xs text-slate-400 mt-1">El estado está marcado como acreditado en el sistema.</p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer Actions */}
                <div className="px-6 py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-between">
                    <p className="text-xs text-slate-500 font-medium">
                        ✓ Válido para visitas futuras en cualquier sucursal
                    </p>
                    <div className="flex gap-2">
                        {fotoUrlFinal && (
                            <button
                                type="button"
                                onClick={handleDescargarFoto}
                                disabled={isDownloading}
                                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-bold text-slate-700 bg-white border border-slate-200 hover:bg-slate-50 transition-colors shadow-xs disabled:opacity-60 cursor-pointer"
                            >
                                {isDownloading ? (
                                    <>
                                        <div className="h-3.5 w-3.5 border-2 border-[#61257b]/30 border-t-[#61257b] rounded-full animate-spin" />
                                        Descargando...
                                    </>
                                ) : (
                                    <>
                                        <Download className="h-4 w-4 text-[#61257b]" />
                                        Descargar Foto
                                    </>
                                )}
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-[#8f1f64] hover:bg-[#c1348b] transition-colors shadow-sm cursor-pointer"
                        >
                            Cerrar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ModalVerDeclaracionJurada;
